import axios from 'axios';
import api from './api';

const API_BASE_URL = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000').trim().replace(/\/$/, '');

export const invoicesAPI = {
  settings: () => api.get('/api/invoices/settings'),
  updateSettings: (data) => api.patch('/api/invoices/settings', data),
  list: (params) => api.get('/api/invoices', { params }),
  get: (id) => api.get(`/api/invoices/${id}`),
  create: (data) => api.post('/api/invoices', data),
  update: (id, data) => api.patch(`/api/invoices/${id}`, data),
  remove: (id) => api.delete(`/api/invoices/${id}`),
  duplicate: (id) => api.post(`/api/invoices/${id}/duplicate`),
  void: (id) => api.post(`/api/invoices/${id}/void`),
  pdf: (id) => api.get(`/api/invoices/${id}/pdf`, { responseType: 'blob' }),
  emailDraft: (id) => api.get(`/api/invoices/${id}/email-draft`),
  send: (id, data) => api.post(`/api/invoices/${id}/send`, data),
  markSent: (id) => api.post(`/api/invoices/${id}/mark-sent`),
  addPayment: (id, data) => api.post(`/api/invoices/${id}/payments`, data),
  removePayment: (id, pid) => api.delete(`/api/invoices/${id}/payments/${pid}`),
  clients: (q) => api.get('/api/invoices/clients', { params: { q } }),
  uploadLogo: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/api/invoices/settings/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  unbilled: (params) => api.get('/api/invoices/unbilled-expenses', { params }),
};

// The client's own link: no login
export const invoicePublicAPI = {
  get: (token, preview) => axios.get(`${API_BASE_URL}/api/invoice-public/${token}`, { params: preview ? { preview: 1 } : {} }),
  pdfUrl: (token) => `${API_BASE_URL}/api/invoice-public/${token}/pdf`,
};

export const expensesAPI = {
  settings: () => api.get('/api/expenses/settings'),
  updateSettings: (data) => api.patch('/api/expenses/settings', data),
  list: (params) => api.get('/api/expenses', { params }),
  get: (id) => api.get(`/api/expenses/${id}`),
  create: (data) => api.post('/api/expenses', data),
  update: (id, data) => api.patch(`/api/expenses/${id}`, data),
  remove: (id) => api.delete(`/api/expenses/${id}`),
  calculate: (data) => api.post('/api/expenses/calculate', data),
  reimbursed: (id, data) => api.post(`/api/expenses/${id}/reimbursed`, data || {}),
  payBill: (id, data) => api.post(`/api/expenses/${id}/pay`, data || {}),
  upcomingBills: () => api.get('/api/expenses/upcoming-bills'),
  uploadReceipts: (id, files) => {
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append('files', f));
    return api.post(`/api/expenses/${id}/receipts`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  receipt: (rid) => api.get(`/api/expenses/receipts/${rid}`, { responseType: 'blob' }),
  removeReceipt: (id, rid) => api.delete(`/api/expenses/${id}/receipts/${rid}`),
  addEntity: (data) => api.post('/api/expenses/entities', data),
  updateEntity: (id, data) => api.patch(`/api/expenses/entities/${id}`, data),
  removeEntity: (id) => api.delete(`/api/expenses/entities/${id}`),
  uploadEntityLogo: (id, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/api/expenses/entities/${id}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  rates: () => api.get('/api/expenses/rates'),
  addRate: (data) => api.post('/api/expenses/rates', data),
  updateRate: (id, data) => api.patch(`/api/expenses/rates/${id}`, data),
  removeRate: (id) => api.delete(`/api/expenses/rates/${id}`),
  applyRate: (id) => api.post(`/api/expenses/rates/${id}/apply`),
  report: (params) => api.get('/api/expenses/report', { params }),
  download: (kind, params) => api.get(`/api/expenses/${kind}`, { params, responseType: 'blob' }),
};

// ── Formatting ───────────────────────────────────────────────────────────────
export const fmtMoney = (v, currency = 'CAD') => {
  const n = Number(v || 0);
  try {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: currency || 'CAD' }).format(n);
  } catch (e) {
    return `$${n.toFixed(2)}`;
  }
};

export const fmtDay = (d, opts = { month: 'short', day: 'numeric', year: 'numeric' }) => {
  if (!d) return '';
  const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-CA', opts);
};

export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const addDaysISO = (iso, n) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d + Number(n || 0));
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

// Open a blob (PDF, receipt) in a new tab, or save it with a filename
export const openBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  if (filename) {
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  } else {
    window.open(url, '_blank');
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// Same totals as the server (finance.compute_totals), for the live preview while editing
export const computeTotals = (items = [], taxes = [], discount = {}, payments = []) => {
  const r2 = (x) => Math.round((x + Number.EPSILON) * 100) / 100;
  const lines = items.map((it) => r2(Number(it.quantity || 0) * Number(it.unit_price || 0)));
  const subtotal = r2(lines.reduce((a, b) => a + b, 0));
  let disc = discount?.type === 'percent' ? r2(subtotal * Number(discount.value || 0) / 100) : r2(Number(discount?.value || 0));
  disc = subtotal > 0 ? Math.max(0, Math.min(disc, subtotal)) : 0;
  const bases = {};
  items.forEach((it, i) => {
    if (!it.tax_id || !taxes.find((t) => t.id === it.tax_id)) return;
    bases[it.tax_id] = (bases[it.tax_id] || 0) + lines[i] - (subtotal ? disc * lines[i] / subtotal : 0);
  });
  const taxLines = taxes.filter((t) => bases[t.id] !== undefined).map((t) => ({ ...t, amount: r2(bases[t.id] * t.rate / 100) }));
  const taxTotal = r2(taxLines.reduce((a, t) => a + t.amount, 0));
  const total = r2(subtotal - disc + taxTotal);
  const paid = r2(payments.reduce((a, p) => a + Number(p.amount || 0), 0));
  return { lines, subtotal, discount: disc, taxes: taxLines, total, paid, balance: r2(total - paid) };
};

export const INVOICE_STATUS = {
  draft: { label: 'Draft', cls: 'bg-[#F3EFE7] text-[#5C6670]' },
  sent: { label: 'Sent', cls: 'bg-sky-50 text-sky-800' },
  viewed: { label: 'Viewed', cls: 'bg-indigo-50 text-indigo-800' },
  partial: { label: 'Part paid', cls: 'bg-amber-50 text-amber-800' },
  overdue: { label: 'Overdue', cls: 'bg-red-50 text-[#A62B1F]' },
  paid: { label: 'Paid', cls: 'bg-emerald-50 text-emerald-800' },
  void: { label: 'Void', cls: 'bg-[#F3EFE7] text-[#94A3B8] line-through' },
};

export const PERIODS = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_quarter', label: 'This quarter' },
  { key: 'last_quarter', label: 'Last quarter' },
  { key: 'ytd', label: 'Year to date' },
  { key: 'last_year', label: 'Last year' },
  { key: 'month', label: 'Pick a month…' },
  { key: 'custom', label: 'Custom dates' },
];

export const thisMonthISO = () => todayISO().slice(0, 7);

// Period picker state -> API params. 'month' sends the calendar month, e.g. period=2026-07.
export const periodParams = (p) => (p.period === 'custom' ? { from_: p.from_ || undefined, to: p.to || undefined }
  : p.period === 'all' ? {} : p.period === 'month' ? { period: p.month || thisMonthISO() } : { period: p.period });

// ── Time tracking ────────────────────────────────────────────────────────────
export const timeAPI = {
  settings: () => api.get('/api/time/settings'),
  updateSettings: (data) => api.patch('/api/time/settings', data),
  rate: (params) => api.get('/api/time/rate', { params }),
  list: (params) => api.get('/api/time', { params }),
  create: (data) => api.post('/api/time', data),
  bulk: (entries) => api.post('/api/time/bulk', { entries }),
  update: (id, data) => api.patch(`/api/time/${id}`, data),
  remove: (id) => api.delete(`/api/time/${id}`),
  timer: () => api.get('/api/time/timer'),
  start: (data) => api.post('/api/time/timer/start', data),
  stop: (data) => api.post('/api/time/timer/stop', data),
  discard: () => api.post('/api/time/timer/discard'),
  report: (params) => api.get('/api/time/report', { params }),
  exportCsv: (params) => api.get('/api/time/export.csv', { params, responseType: 'blob' }),
  reportPdf: (params) => api.get('/api/time/report.pdf', { params, responseType: 'blob' }),
};
invoicesAPI.unbilledTime = (params) => api.get('/api/invoices/unbilled-time', { params });
invoicesAPI.appliedTime = (id) => api.get(`/api/invoices/${id}/applied-time`);
invoicesAPI.applyTime = (id, ids) => api.post(`/api/invoices/${id}/applied-time`, { time_entry_ids: ids });
invoicesAPI.unapplyTime = (id, ids) => api.post(`/api/invoices/${id}/applied-time/remove`, { time_entry_ids: ids });
invoicesAPI.attachments = (id) => api.get(`/api/invoices/${id}/attachments`);
invoicesAPI.uploadAttachments = (id, files) => {
  const fd = new FormData();
  Array.from(files).forEach((f) => fd.append('files', f));
  return api.post(`/api/invoices/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
invoicesAPI.orderAttachments = (id, ids) => api.post(`/api/invoices/${id}/attachments/order`, { ids });
invoicesAPI.removeAttachment = (id, aid) => api.delete(`/api/invoices/${id}/attachments/${aid}`);
invoicesAPI.attachment = (id, aid) => api.get(`/api/invoices/${id}/attachments/${aid}`, { responseType: 'blob' });
invoicesAPI.packagePdf = (id) => api.get(`/api/invoices/${id}/package`, { responseType: 'blob' });

// "1.5", "1:30", "90m", "1h30", "1h 30m", "2h" -> 1.5 (hours), or null
export const parseHours = (v) => {
  const s = String(v ?? '').trim().toLowerCase().replace(',', '.');
  if (!s) return null;
  if (/^\d+(\.\d+)?$/.test(s)) return Number(s) > 0 ? Number(s) : null;      // a plain number is hours
  let m = s.match(/^(\d+):(\d{1,2})$/);
  if (m) return Number(m[1]) + Number(m[2]) / 60;
  m = s.match(/^(\d+(?:\.\d+)?)\s*h(?:rs?|ours?)?\s*(?:(\d+)\s*(?:m(?:in(?:s|utes?)?)?)?)?$/);   // 2h, 1h30, 1h 30m
  if (m) return Number(m[1]) + Number(m[2] || 0) / 60 || null;
  m = s.match(/^(\d+)\s*m(?:in(?:s|utes?)?)?$/);                                      // 90m, 45 min
  if (m) return Number(m[1]) / 60 || null;
  return null;
};

export const fmtHours = (h) => {
  const n = Number(h || 0);
  const hh = Math.floor(n); const mm = Math.round((n - hh) * 60);
  return mm === 60 ? `${hh + 1}h` : mm ? `${hh}h ${String(mm).padStart(2, '0')}m` : `${hh}h`;
};

// Same order as the server: project rate, then client rate, then the default
export const rateFor = (settings, projectId, contactId) => {
  const rates = settings?.rates || [];
  const p = projectId && rates.find((r) => r.scope === 'project' && r.ref_id === projectId);
  if (p) return { rate: p.rate, source: 'project rate' };
  const c = contactId && rates.find((r) => r.scope === 'client' && r.ref_id === contactId);
  if (c) return { rate: c.rate, source: 'client rate' };
  return { rate: Number(settings?.default_rate || 0), source: 'default rate' };
};

// ── Quotes & proposals ───────────────────────────────────────────────────────
export const quotesAPI = {
  settings: () => api.get('/api/quotes/settings'),
  updateSettings: (data) => api.patch('/api/quotes/settings', data),
  list: (params) => api.get('/api/quotes', { params }),
  get: (id) => api.get(`/api/quotes/${id}`),
  create: (data) => api.post('/api/quotes', data),
  update: (id, data) => api.patch(`/api/quotes/${id}`, data),
  remove: (id) => api.delete(`/api/quotes/${id}`),
  revise: (id) => api.post(`/api/quotes/${id}/revise`),
  changeOrder: (id) => api.post(`/api/quotes/${id}/change-order`),
  pdf: (id) => api.get(`/api/quotes/${id}/pdf`, { responseType: 'blob' }),
  emailDraft: (id) => api.get(`/api/quotes/${id}/email-draft`),
  send: (id, data) => api.post(`/api/quotes/${id}/send`, data),
  accept: (id, data) => api.post(`/api/quotes/${id}/accept`, data),
  decline: (id, data) => api.post(`/api/quotes/${id}/decline`, data),
  bill: (id, data) => api.post(`/api/quotes/${id}/invoice`, data),
  attachments: (id) => api.get(`/api/quotes/${id}/attachments`),
  uploadAttachments: (id, files) => {
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append('files', f));
    return api.post(`/api/quotes/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  orderAttachments: (id, ids) => api.post(`/api/quotes/${id}/attachments/order`, { ids }),
  removeAttachment: (id, aid) => api.delete(`/api/quotes/${id}/attachments/${aid}`),
  attachment: (id, aid) => api.get(`/api/quotes/${id}/attachments/${aid}`, { responseType: 'blob' }),
};

export const quotePublicAPI = {
  get: (token, preview) => axios.get(`${API_BASE_URL}/api/quote-public/${token}`, { params: preview ? { preview: 1 } : {} }),
  pdfUrl: (token) => `${API_BASE_URL}/api/quote-public/${token}/pdf`,
  accept: (token, data) => axios.post(`${API_BASE_URL}/api/quote-public/${token}/accept`, data),
  decline: (token, data) => axios.post(`${API_BASE_URL}/api/quote-public/${token}/decline`, data),
};

export const QUOTE_STATUS = {
  draft: { label: 'Draft', cls: 'bg-[#F3EFE7] text-[#5C6670]' },
  sent: { label: 'Sent', cls: 'bg-sky-50 text-sky-800' },
  viewed: { label: 'Viewed', cls: 'bg-indigo-50 text-indigo-800' },
  accepted: { label: 'Accepted', cls: 'bg-emerald-50 text-emerald-800' },
  declined: { label: 'Declined', cls: 'bg-red-50 text-[#A62B1F]' },
  expired: { label: 'Expired', cls: 'bg-amber-50 text-amber-800' },
  superseded: { label: 'Replaced', cls: 'bg-[#F3EFE7] text-[#94A3B8] line-through' },
};
